import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../contexts/NotificationContext';
import { supabase } from '../../services/supabase';
import { usePermissions } from '../../contexts/PermissionsContext';
import { Company } from '../../types/company.types';
import { Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface CustobarIntegrationProps {
  companyId: string;
  company: Company;
  onCompanyUpdate: (updatedCompany: Company) => void;
}

const CustobarIntegration = ({ companyId, company, onCompanyUpdate }: CustobarIntegrationProps) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { hasAdminAccess } = usePermissions();

  const [savingCustobar, setSavingCustobar] = useState(false);
  const [togglingIngestion, setTogglingIngestion] = useState(false);

  const [custobarData, setCustobarData] = useState({
    custobar_api_key: '',
    custobar_company_name: '',
  });

  // Initialize custobar data when company changes
  useEffect(() => {
    setCustobarData({
      custobar_api_key: company.custobar_api_key || '',
      custobar_company_name: company.custobar_company_name || '',
    });
  }, [company]);

  const handleCustobarChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCustobarData((prev) => ({ ...prev, [name]: value }));
  };

  const saveCustobarConfig = async () => {
    setSavingCustobar(true);
    try {
      const { error } = await supabase
        .from('companies')
        .update({
          custobar_api_key: custobarData.custobar_api_key,
          custobar_company_name: custobarData.custobar_company_name,
        })
        .eq('id', companyId);

      if (error) throw error;

      showNotification(t('integrations.custobar_config_saved'), 'success');

      // Update parent component with new company data
      const updatedCompany = {
        ...company,
        custobar_api_key: custobarData.custobar_api_key,
        custobar_company_name: custobarData.custobar_company_name,
      };
      onCompanyUpdate(updatedCompany);
    } catch (error) {
      console.error('Error saving Custobar config:', error);
      showNotification(t('integrations.error_saving_custobar_config'), 'error');
    } finally {
      setSavingCustobar(false);
    }
  };

  const toggleDataIngestion = async () => {
    setTogglingIngestion(true);

    try {
      // First save the Custobar configuration
      const { error: saveError } = await supabase
        .from('companies')
        .update({
          custobar_api_key: custobarData.custobar_api_key,
          custobar_company_name: custobarData.custobar_company_name,
        })
        .eq('id', companyId);

      if (saveError) throw saveError;

      // Then call the toggle function
      const { data, error } = await supabase.functions.invoke('toggle-custobar-data-ingestion', {
        body: {
          companyId,
          enableIngestion: !company.allow_ingestion,
        },
      });

      if (error) throw error;

      if (data?.success) {
        showNotification(
          !company.allow_ingestion
            ? t('integrations.data_ingestion_enabled')
            : t('integrations.data_ingestion_disabled'),
          'success'
        );

        // Refresh company data from database
        const { data: updatedCompany, error: fetchError } = await supabase
          .from('companies')
          .select('*')
          .eq('id', companyId)
          .single();

        if (fetchError) throw fetchError;

        onCompanyUpdate(updatedCompany);
      } else {
        showNotification(t('integrations.error_toggling_data_ingestion'), 'error');
      }
    } catch (error) {
      console.error('Error toggling data ingestion:', error);
      showNotification(t('integrations.error_toggling_data_ingestion'), 'error');
    } finally {
      setTogglingIngestion(false);
    }
  };

  const canEdit = hasAdminAccess(companyId);

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="custobar-content"
        id="custobar-header"
      >
        <div className="flex items-center justify-between w-full mr-4">
          <div>
            <Typography variant="h6" component="h4">
              {t('integrations.custobar_integration')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('integrations.custobar_description')}
            </Typography>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div
                className={`w-3 h-3 rounded-full mr-2 ${
                  company.custobar_api_key && company.custobar_company_name
                    ? 'bg-green-500'
                    : 'bg-gray-400'
                }`}
              ></div>
              <span className="text-sm text-gray-600">
                {company.custobar_api_key && company.custobar_company_name
                  ? t('integrations.configured')
                  : t('integrations.not_configured')}
              </span>
            </div>
            <div className="flex items-center">
              <div
                className={`w-3 h-3 rounded-full mr-2 ${
                  company.allow_ingestion ? 'bg-green-500' : 'bg-gray-400'
                }`}
              ></div>
              <span className="text-sm text-gray-600">
                {company.allow_ingestion ? t('integrations.enabled') : t('integrations.disabled')}
              </span>
            </div>
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('company.custobar_company_name')}
            </label>
            <input
              type="text"
              name="custobar_company_name"
              value={custobarData.custobar_company_name}
              onChange={handleCustobarChange}
              disabled={!canEdit}
              className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-50 disabled:text-gray-500"
              placeholder={t('integrations.custobar_company_name_placeholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('company.custobar_api_key')}
            </label>
            <textarea
              rows={3}
              name="custobar_api_key"
              value={custobarData.custobar_api_key}
              onChange={handleCustobarChange}
              disabled={!canEdit}
              className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-50 disabled:text-gray-500"
              placeholder={t('integrations.custobar_api_key_placeholder')}
            />
          </div>

          {canEdit && (
            <div className="flex flex-wrap gap-3 pt-4 justify-end">
              <button
                onClick={saveCustobarConfig}
                disabled={savingCustobar}
                className="px-4 py-2 bg-blue-600  text-sm text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingCustobar ? t('common.saving') : t('common.save')}
              </button>

              <button
                onClick={toggleDataIngestion}
                disabled={togglingIngestion}
                className={`px-4 py-2 text-white text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                  company?.allow_ingestion
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-orange-600 hover:bg-orange-700'
                }`}
              >
                {togglingIngestion
                  ? t('integrations.updating')
                  : company?.allow_ingestion
                    ? t('integrations.disable_data_ingestion')
                    : t('integrations.enable_data_ingestion')}
              </button>
            </div>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default CustobarIntegration;
